import { FilterService, FilterMetadata } from 'primeng/api';

import { GridFilterSelectItem, GridColumn, SortRequest, FilterValuesResponse, FieldFilterRequest, FilterSortPageRequest, GlobalFilterRequest, FilterValuesRequest, ShowFilterEvent, GridFilterMatchModes } from './grid.model';
import { getPropertyValue } from './string-operations';
import { dateConvert } from "./date-operations";
import { ArrayProcessor } from './array.processor';
import { TableLazyLoadEvent } from "primeng/table";

export abstract class GridProcessor {
  private static arrayInAllFilter(values: any, filter: any, filterLocale?: string): boolean {
    if (!filter || !Array.isArray(filter) || filter.length === 0)
      return true;

    if (values === undefined)
      return false;

    if (Array.isArray(values)) {
      if (values.length === 0)
        return false;

      return filter.every(value => values.every(value => value.every(value.indexOf(value)) !== -1));
    }

    return filter.every(value => value.every(value => value === values));
  }

  private static arrayInAnyFilter(values: any, filter: any, filterLocale?: string): boolean {
    if (!filter || !Array.isArray(filter) || filter.length === 0)
      return true;

    if (values === undefined)
      return false;

    if (Array.isArray(values)) {
      if (values.length === 0)
        return false;

      return filter.some(value => value.some(value => values.indexOf(value) !== -1));
    }

    return filter.some(value => value.some(value => value === values));
  }

  private static dateFilter(values: any, filter: any, filterLocale?: string): boolean {
    if (!filter || !Array.isArray(filter) || filter.length === 0)
      return true;

    if (values === undefined)
      return false;

    if (Array.isArray(values)) {
      if (values.length === 0)
        return false;

      return filter.some(value => values.map(t => dateConvert(t)?.getTime()).indexOf(dateConvert(value)?.getTime()) !== -1);
    }

    const dateValue = dateConvert(values)?.getTime();
    return filter.some(v1 => v1.some(v2 => dateConvert(v2)?.getTime() === dateValue));
  }

  public static registerCustomFilters(filterService: FilterService) {
    filterService.register(GridFilterMatchModes.arrayInAll, (value: any, filter: any, filterLocale?: any): boolean => GridProcessor.arrayInAllFilter(value, filter, filterLocale));
    filterService.register(GridFilterMatchModes.arrayInAny, (value: any, filter: any, filterLocale?: any): boolean => GridProcessor.arrayInAnyFilter(value, filter, filterLocale));
    filterService.register(GridFilterMatchModes.date, (value: any, filter: any, filterLocale?: any): boolean => GridProcessor.dateFilter(value, filter, filterLocale));
  }

  private static hasCustomMatchMode(col: GridColumn): boolean {
    return col.filter.matchMode === GridFilterMatchModes.arrayInAny ||
      col.filter.matchMode === GridFilterMatchModes.arrayInAll ||
      col.filter.matchMode === GridFilterMatchModes.date;
  }

  static resetFilters(cols: Array<GridColumn>, action: (col: GridColumn) => void = null): void {
    for (const col of cols) {
      if (action) {
        action(col);
      }
      if (col.filter) {
        col.filter.selectedValues = null;
      }
    }
  }

  static createFilterSortPageRequest<TRequest>(
    request: TRequest,
    event: TableLazyLoadEvent,
    cols: Array<GridColumn>,
    globalFilterFields: Array<string>,
    globalFilterMatchMode: string): FilterSortPageRequest<TRequest> {
    const filterSortPageRequest = new FilterSortPageRequest<TRequest>({
      data: request,
      first: event.first,
      rows: event.rows,
      sorts: event.multiSortMeta
    });

    filterSortPageRequest.filterFields = GridProcessor.getFilterFields(cols);

    filterSortPageRequest.filters = GridProcessor.getFilters(cols, event.filters, filterSortPageRequest.filters);

    if (event.globalFilter)
      filterSortPageRequest.globalFilter = GridProcessor.createGlobalFilterRequest(
        event.globalFilter,
        globalFilterFields,
        globalFilterMatchMode);

    return filterSortPageRequest;
  }

  static createFilterValuesRequest<TRequest>(
    request: TRequest,
    event: ShowFilterEvent,
    cols: Array<GridColumn>,
    globalFilterFields: Array<string>,
    globalFilterMatchMode: string): FilterValuesRequest<TRequest> {
    const filterValuesRequest = new FilterValuesRequest<TRequest>({
      data: request,
      field: (event.col.filter && event.col.filter.field) || event.col.field
    });

    filterValuesRequest.filterFields = GridProcessor.getFilterFields(cols);

    filterValuesRequest.filters = GridProcessor.getFilters(cols, event.filters, filterValuesRequest.filters);

    if (event.globalFilter)
      filterValuesRequest.globalFilter = GridProcessor.createGlobalFilterRequest(
        event.globalFilter,
        globalFilterFields,
        globalFilterMatchMode);

    return filterValuesRequest;
  }

  private static getFilterFields(cols: Array<GridColumn>): Array<string> {
    return cols
      .filter(col => {
        if (col.filter) {
          const selectedValues = col.filter.selectedValues;
          return (!selectedValues || selectedValues.length === 0);
        } else {
          return false;
        }
      })
      .map(col => (col.filter && col.filter.field) || col.field);
  }

  public static getFilters(
    cols: Array<GridColumn>,
    eventFilters: { [s: string]: FilterMetadata | FilterMetadata[] },
    requestFilters: Array<FieldFilterRequest>): Array<FieldFilterRequest> {
    if (eventFilters) {
      for (const field in eventFilters) {
        if (eventFilters.hasOwnProperty(field) && field !== 'global') {
          const label = this.getFilterHeader(cols, field);
          requestFilters = requestFilters || new Array<FieldFilterRequest>();
          let value = eventFilters[field][0].value;
          if (Array.isArray(value)) {
            const col = cols.find(c => (c.filter && c.filter.field === field) || c.field === field);
            let array = value.flat(Infinity).map(v => col.filter.convertValue(v));
            if (array[0] instanceof Date)
              array = array.filter((v, i, a) => a.findIndex(t => t.getTime() === v.getTime()) === i);
            else
              array = array.filter((v, i, a) => a.indexOf(v) === i);
            value = array;
          }
          requestFilters.push(new FieldFilterRequest({
            label: label,
            field: field,
            value: value,
            matchMode: eventFilters[field][0].matchMode
          }));
        }
      }
    }

    return requestFilters;
  }

  private static createGlobalFilterRequest(
    globalFilter: any,
    globalFilterFields: Array<string>,
    globalFilterMatchMode: string): GlobalFilterRequest {
    return new GlobalFilterRequest({
      fields: globalFilterFields,
      value: globalFilter,
      matchMode: globalFilterMatchMode
    });
  }

  static processFilterValuesResponse(
    col: GridColumn,
    filterValuesResponse: FilterValuesResponse): void {
    col.filter.availableValues = GridProcessor.getFilterValues(col, filterValuesResponse.values);
  }

  private static getFilterValues(col: GridColumn, values: any[]) {
    if (col && col.filter && values) {
      const items = new Array<GridFilterSelectItem>();

      values.forEach(value => {
        const val = col.filter.convertValue(value);
        const label = (col.filter.formatValue || col.formatValue)(val);
        let item = items.find(i => i.label === label);
        if (item)
          item.value.push(val);
        else {
          item = new GridFilterSelectItem({
            label: label,
            value: [val]
          });
          items.push(item);
        }
      });
      items.forEach(item => item.value = ArrayProcessor.distinct(item.value));
      return items;
    }
  }

  static extractColumnFilters<TData>(cols: Array<GridColumn>, rows: Array<TData>): TData[] {
    const filteredRows = rows.filter(row => {
      for (const col of cols) {
        if (col.filter) {
          const cellValue = getPropertyValue(row, col.filter.field || col.field);
          const selectedValues = col.filter.selectedValues?.flat();
          if (selectedValues && selectedValues.length !== 0 &&
            !selectedValues.some(value =>
              (Array.isArray(cellValue)
                ? cellValue.indexOf(value) !== -1
                : value === cellValue) as any)) {
            return false;
          }
        }
      }
      return true;
    });

    for (const col of cols) {
      if (col.filter) {
        let selectedValues = col.filter.selectedValues?.flat();
        let availableValues = filteredRows.map(row => getPropertyValue(row, col.filter.field || col.field)).flat();
        if (selectedValues && selectedValues.length !== 0) {
          if (selectedValues.every(t => Array.isArray(t?.value)))
            selectedValues = selectedValues.map(t => t.value);

          let filterAvailableValues = col.filter.availableValues.map(filterSelectItem => filterSelectItem.value);
          if (filterAvailableValues.length > 0 && filterAvailableValues.every(t => Array.isArray(t?.value)))
            filterAvailableValues = filterAvailableValues.map(t => t.value);

          availableValues = selectedValues.flat()
            .concat(availableValues)
            .concat(filterAvailableValues.flat());
        }

        availableValues = Array.from(new Set(availableValues));
        availableValues = ArrayProcessor.sort(availableValues);

        if (col.filter.convertValue)
          availableValues = availableValues.map(col.filter.convertValue);

        if (this.hasCustomMatchMode(col))
          col.filter.availableValues = GridProcessor.getFilterValues(col, availableValues);
        else {
          col.filter.availableValues = availableValues.map(value => new GridFilterSelectItem({
            label: (col.filter.formatValue || col.formatValue)(value),
            value: value === undefined ? null : value
          }));
        }
      }
    }
    return filteredRows;
  }

  static sort<TData>(sorts: Array<SortRequest>, rows: Array<TData>): Array<TData> {
    if (!sorts || (sorts && sorts.length === 0)) {
      return rows;
    }
    return rows.sort((data1: TData, data2: TData) => this.compareFields<TData, TData>(data1, data2, sorts, 0));
  }

  private static compareFields<TData1, TData2>(data1: TData1, data2: TData2, sorts: Array<SortRequest>, index: number): number {
    const value1: any = getPropertyValue(data1, sorts[index].field);
    const value2: any = getPropertyValue(data2, sorts[index].field);
    let result = null;

    if (value1 === null && value2 !== null) {
      result = -1;
    } else if (value1 !== null && value2 === null) {
      result = 1;
    } else if (value1 === null && value2 === null) {
      result = 0;
    }

    if (typeof value1 === 'string' || value1 instanceof String) {
      if (value1.localeCompare && (value1 !== value2)) {
        return (sorts[index].order * value1.localeCompare(value2));
      }
    } else {
      result = (value1 < value2) ? -1 : 1;
    }

    if (value1 === value2) {
      return (sorts.length - 1) > index ? this.compareFields(data1, data2, sorts, index + 1) : 0;
    }

    return (sorts[index].order * result);
  }

  private static getFilterHeader(cols: Array<GridColumn>, field: string): string {
    for (let index = 0; index < cols.length; index++) {
      let col = cols[index];
      if (col.field === field && col.header)
        return col.header;
      else if (col.filter && col.filter.field === field && col.header)
        return col.header;
    }
    return field;
  }
}
